import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';

import {
  DataGridAsyncSelectFilter,
  DataGridMultiSelectFilter,
  DataGridSingleSelectFilter,
} from '@sophocles/data-grids/components/filters';
import { ClearAllChip, ToolbarIconButton, toolbarChipSx } from '@sophocles/data-grids/components/toolbar';

import { stripCountPrefix } from './filterOptionLabel';
import FilterStaffChip from './FilterStaffChip';
import FilterValuePanel from './FilterValuePanel';

/**
 * A filter bar with two zones:
 *
 *   1. **Quick filters** – rendered inline as chip-style dropdowns (the most
 *      commonly used filters for a given grid). These use the existing
 *      DataGridSingleSelectFilter / DataGridMultiSelectFilter components.
 *
 *   2. **More filters** – opened via a TuneRounded icon button. Shows a
 *      two-panel popover (field list → value panel) for all remaining filters.
 *      Active "more" filters render as "Field is Value ×" pills.
 *
 * On mobile (< md) both zones collapse behind a single "Filters" button that
 * opens a bottom sheet: a list of every filter → drill into each one's value
 * panel, with Clear-all / Done in the footer.
 *
 * Supported filter `type` values: 'single' | 'multi' | 'tree' | 'boolean' | 'daterange' | 'async'.
 * An 'async' filter's vocabulary lives on the server rather than in `options`: it supplies
 * `fetchPage({ search, offset, limit, signal })`, optionally `resolveValue(value, { signal })`
 * or `selectedLabel` to name the current selection, optionally
 * `renderOption(option, searchText)` to draw a richer row than the bare label, and is
 * single-select.
 * For 'daterange', value is `{ from: Date|null, to: Date|null } | null`. For 'tree', value
 * is a flat array of selected CHILD values, and options is
 * `[{ value, label, children: [{ value, label }] }]` — parent nodes are a selection
 * convenience only (checking one selects/deselects all its children); pass `countLabel(count)`
 * to control the active-pill summary (defaults to "N selected"). 'tree' is a moreFilters-only
 * type — the quick-filter chip row has no tree affordance.
 *
 * Props:
 *   quickFilters – array of filter defs rendered inline
 *     Each: { key, label, type, options, value, onChange, allLabel?, chipLabel?, disabled? }
 *
 *   moreFilters  – array of filter defs behind the popover
 *     Each: { key, label, type, options, value, onChange, countLabel?, staffOnly? }
 *     `staffOnly` badges a control only staff are offered.
 *
 *   onResetAll   – callback to clear all filters (quick + more)
 *
 *   tabs         – optional element rendered at the leading edge (typically a <Tabs>
 *                  component). Separated from chips with a vertical divider.
 *
 *   search       – optional element rendered after tabs/divider but before chips.
 *                  Typically a search TextField. Receives `flex: 1` via wrapper.
 *
 *   trailing     – optional element rendered after the Clear-all chip (e.g. icon
 *                  buttons that belong with the filter row).
 *
 *   dense        – when true, tightens the row gap. Useful when the bar is
 *                  embedded in a constrained surface (tabbed grid toolbar).
 *
 *   sx           – optional MUI sx overrides for the root row.
 */

/** True when a filter holds a non-default, active value. */
function isFilterActive(filter) {
  if (filter.type === 'daterange') {
    return filter.value != null && (filter.value.from != null || filter.value.to != null);
  }
  if (Array.isArray(filter.value)) return filter.value.length > 0;
  return filter.value != null && filter.value !== filter.defaultValue;
}

/** Human-readable summary of a filter's current value (for pills + the mobile list). */
function formatFilterValue(filter) {
  if (filter.type === 'boolean') {
    return filter.value === 'true' ? 'Yes' : 'No';
  }
  if (filter.type === 'single') {
    const opt = filter.options?.find(o => o.value === filter.value);
    return stripCountPrefix(opt?.label ?? String(filter.value));
  }
  // An async filter's options live on the server, so the caller carries the
  // chosen one's name; a bare id is all there is to show without it.
  if (filter.type === 'async') {
    return filter.selectedLabel ?? String(filter.value ?? '');
  }
  if (filter.type === 'multi' && Array.isArray(filter.value)) {
    const parts = filter.value.map(v => {
      const opt = filter.options?.find(o => o.value === v);
      return stripCountPrefix(opt?.label ?? String(v));
    });
    if (parts.every(part => typeof part === 'string')) return parts.join(', ');
    // JSX labels can't be string-joined; interleave the separators as children.
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 ? ', ' : null}
        {part}
      </React.Fragment>
    ));
  }
  if (filter.type === 'tree' && Array.isArray(filter.value)) {
    // A tree's selection can span dozens of leaves — a joined label list reads
    // as noise, so this summarizes as a count instead (caller-controlled
    // wording via countLabel; a generic default otherwise).
    const count = filter.value.length;
    return filter.countLabel ? filter.countLabel(count) : `${count} selected`;
  }
  if (filter.type === 'daterange') {
    const fmt = d => format(d, 'MMM d, yyyy');
    const { from, to } = filter.value || {};
    if (from && to) return `${fmt(from)} – ${fmt(to)}`;
    if (from) return `since ${fmt(from)}`;
    if (to) return `before ${fmt(to)}`;
    return '';
  }
  return String(filter.value);
}

export default function FilterBar({
  tabs = null,
  search = null,
  quickFilters = [],
  moreFilters = [],
  onResetAll,
  trailing = null,
  dense = false,
  sx = null,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  // Mobile bottom-sheet state. `sheetField` null → the filter list; set → that
  // filter's value panel.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetField, setSheetField] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = useCallback(event => {
    setAnchorEl(event.currentTarget);
    setSelectedField(null);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedField(null);
  }, []);

  const handleApply = useCallback(
    (key, value) => {
      const filter = moreFilters.find(f => f.key === key);
      if (filter) filter.onChange(value);
      handleClose();
    },
    [moreFilters, handleClose],
  );

  const activeMoreFilters = useMemo(() => moreFilters.filter(isFilterActive), [moreFilters]);

  const activeMoreCount = activeMoreFilters.length;
  const activeQuickCount = useMemo(() => quickFilters.filter(isFilterActive).length, [quickFilters]);
  const totalActiveCount = activeQuickCount + activeMoreCount;
  const hasAnyActive = totalActiveCount > 0;

  const selectedFilterDef = moreFilters.find(f => f.key === selectedField);

  const hasChipArea = quickFilters.length > 0 || moreFilters.length > 0;
  const rowGap = dense ? 0.5 : 'var(--toolbar-gap)';

  // Mobile sheet works over the combined filter set.
  const allFilters = useMemo(() => [...quickFilters, ...moreFilters], [quickFilters, moreFilters]);
  const sheetFilterDef = allFilters.find(f => f.key === sheetField);

  const openSheet = useCallback(() => {
    setSheetField(null);
    setSheetOpen(true);
  }, []);

  const handleSheetApply = (key, value) => {
    const filter = allFilters.find(f => f.key === key);
    if (filter) filter.onChange(value);
    setSheetField(null);
  };

  const sxList = Array.isArray(sx) ? sx : [sx].filter(Boolean);

  return (
    <Box sx={[{ display: 'flex', flexWrap: 'wrap', gap: rowGap, alignItems: 'center', width: '100%' }, ...sxList]}>
      {tabs}
      {tabs && (hasChipArea || search) && <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />}

      {search && <Box sx={{ flex: 1, minWidth: 180, display: 'flex', alignItems: 'center' }}>{search}</Box>}

      {/* Desktop: inline quick chips + active "more" pills + More-filters button */}
      {hasChipArea && !isMobile && (
        <>
          {quickFilters.map(filter => (
            <QuickFilter key={filter.key} filter={filter} />
          ))}

          {activeMoreFilters.map(filter => (
            <ActiveFilterChip key={filter.key} filter={filter} />
          ))}

          {moreFilters.length > 0 && (
            <ToolbarIconButton tooltip="More filters" onClick={handleOpen}>
              <Badge badgeContent={activeMoreCount} color="primary" variant="dot" invisible={activeMoreCount === 0}>
                <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
              </Badge>
            </ToolbarIconButton>
          )}

          {hasAnyActive && <ClearAllChip onClick={onResetAll} />}
        </>
      )}

      {/* Mobile: a single Filters button that opens the bottom sheet */}
      {hasChipArea && isMobile && (
        <Chip
          icon={<FilterAltOutlinedIcon sx={{ fontSize: 16 }} />}
          label={totalActiveCount > 0 ? `Filters (${totalActiveCount})` : 'Filters'}
          size="small"
          variant={totalActiveCount > 0 ? 'filled' : 'outlined'}
          onClick={openSheet}
          sx={toolbarChipSx}
        />
      )}

      {trailing && (
        <Box sx={{ ml: search ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: rowGap }}>{trailing}</Box>
      )}

      {/* Desktop more-filters popover */}
      {!isMobile && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 'var(--filter-popover-radius)', overflow: 'hidden' } } }}
        >
          <Stack direction="row" sx={{ minHeight: 280 }}>
            <Box sx={{ width: 180, borderRight: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block' }}>
                Filter by
              </Typography>
              <List dense disablePadding sx={{ py: 0.5 }}>
                {moreFilters.map(filter => (
                  <ListItemButton
                    key={filter.key}
                    selected={selectedField === filter.key}
                    onClick={() => setSelectedField(filter.key)}
                    sx={{ px: 2, py: 0.75 }}
                  >
                    <ListItemText
                      primary={
                        <>
                          {filter.label}
                          {filter.staffOnly ? <FilterStaffChip /> : null}
                        </>
                      }
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          fontWeight: selectedField === filter.key ? 600 : 400,
                          sx: { display: 'flex', alignItems: 'center' },
                        },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            <Box sx={{ width: 260 }}>
              {selectedFilterDef ? (
                <FilterValuePanel
                  key={selectedFilterDef.key}
                  filter={selectedFilterDef}
                  onApply={value => handleApply(selectedField, value)}
                  onCancel={handleClose}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Select a field
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Popover>
      )}

      {/* Mobile filter bottom sheet */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 'var(--radius-lg)',
                borderTopRightRadius: 'var(--radius-lg)',
                maxHeight: '80vh',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                {sheetField && (
                  <IconButton size="small" onClick={() => setSheetField(null)} aria-label="Back" sx={{ ml: -0.5 }}>
                    <ChevronLeftIcon />
                  </IconButton>
                )}
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {sheetField ? (sheetFilterDef?.label ?? 'Filter') : 'Filters'}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={() => setSheetOpen(false)} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider />

            {sheetField && sheetFilterDef ? (
              <FilterValuePanel
                key={sheetFilterDef.key}
                filter={sheetFilterDef}
                onApply={value => handleSheetApply(sheetField, value)}
                onCancel={() => setSheetField(null)}
              />
            ) : (
              <>
                <List disablePadding sx={{ overflow: 'auto' }}>
                  {allFilters.map(filter => (
                    <ListItemButton
                      key={filter.key}
                      onClick={() => setSheetField(filter.key)}
                      disabled={filter.disabled}
                      sx={{ px: 2, py: 1 }}
                    >
                      <ListItemText
                        primary={filter.label}
                        secondary={isFilterActive(filter) ? formatFilterValue(filter) : undefined}
                        // A long selection truncates rather than pushing the
                        // chevron off the row; tapping through shows it in full.
                        slotProps={{
                          primary: { noWrap: true },
                          secondary: { color: 'primary.main', noWrap: true },
                        }}
                        sx={{ minWidth: 0, pr: 1 }}
                      />
                      <ChevronRightIcon sx={{ color: 'text.disabled' }} />
                    </ListItemButton>
                  ))}
                </List>
                <Divider />
                <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'space-between' }}>
                  <Button size="small" onClick={onResetAll} disabled={!hasAnyActive}>
                    Clear all
                  </Button>
                  <Button size="small" variant="contained" onClick={() => setSheetOpen(false)}>
                    Done
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        </Drawer>
      )}
    </Box>
  );
}

/**
 * Renders a quick filter as an inline chip dropdown.
 * Delegates to DataGridSingleSelectFilter or DataGridMultiSelectFilter
 * depending on filter.type.
 */
function QuickFilter({ filter }) {
  if (filter.type === 'async') {
    return (
      <DataGridAsyncSelectFilter
        label={filter.label}
        value={filter.value}
        onChange={filter.onChange}
        fetchPage={filter.fetchPage}
        resolveValue={filter.resolveValue}
        selectedLabel={filter.selectedLabel}
        allLabel={filter.allLabel}
        searchPlaceholder={filter.searchPlaceholder}
        renderOption={filter.renderOption}
        chipLabel={filter.chipLabel}
        disabled={filter.disabled}
        icon={filter.icon}
      />
    );
  }

  if (filter.type === 'multi') {
    return (
      <DataGridMultiSelectFilter
        label={filter.label}
        values={filter.value || []}
        onChange={filter.onChange}
        options={filter.options || []}
        disabled={filter.disabled}
        icon={filter.icon}
      />
    );
  }

  // single + boolean both use single-select
  return (
    <DataGridSingleSelectFilter
      label={filter.label}
      value={filter.value}
      onChange={filter.onChange}
      allLabel={filter.allLabel}
      options={filter.options || []}
      chipLabel={filter.chipLabel}
      renderOption={filter.renderOption}
      disabled={filter.disabled}
      icon={filter.icon}
    />
  );
}

function ActiveFilterChip({ filter }) {
  const displayValue = formatFilterValue(filter);

  const handleDelete = useCallback(() => {
    if (filter.type === 'multi' || filter.type === 'tree') {
      filter.onChange([]);
    } else {
      filter.onChange(null);
    }
  }, [filter]);

  return (
    <Chip
      size="small"
      label={
        // A multi-select of long names would otherwise push the bar wide and
        // the clear button off the end of it; the pill keeps its shape and
        // the full selection stays available on hover.
        <Box
          component="span"
          title={typeof displayValue === 'string' ? `${filter.label} is ${displayValue}` : undefined}
          sx={{ display: 'block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          <strong>{filter.label}</strong> is {displayValue}
        </Box>
      }
      onDelete={handleDelete}
      deleteIcon={<ClearIcon sx={{ fontSize: 14 }} />}
      sx={{
        height: 'var(--filter-chip-height)',
        borderRadius: 'var(--filter-chip-radius)',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.selected',
        boxShadow: 'var(--card-shadow-raised)',
        '& .MuiChip-label': { px: 1, fontSize: 'var(--filter-chip-font)' },
      }}
    />
  );
}
